import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocalStorage } from '@df/browser/storage/decorator/local-storage';
import { recentlyViewedConfig } from '@df/catalog-ui/recently-viewed/recently-viewed.config';
import { RecentlyViewed } from '@df/catalog-ui/recently-viewed/recently-viewed.interfaces';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import without from 'lodash-es/without';

/**
 * Service holding information about recently viewed products
 *
 * It is initiated in init.run.js by calling checkRoute whenever viewed page changes.
 * To retrieve data use `model` object: model.data will be array of ids of recently viewed products.
 *
 * Limit is by default 15, you can change it using with App config `catalog.recentlyViewedLimit`
 */
@Injectable({
    providedIn: 'root'
})
export class RecentlyViewedService {
    /**
     * Local storage collectiojn
     */
    @LocalStorage('recentlyViewed')
    private _collection: number[] | undefined;

    /**
     * Local storage slot id
     */
    protected storageId = 'recentlyViewed';

    /**
     * Max items getter
     */
    private get limit(): number | undefined {
        return recentlyViewedConfig.limit;
    }

    private meta = inject(Meta);
    private app = inject(App);
    private destroyRef = inject(DestroyRef);

    appInitialize() {
        this.meta.subject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onMetaChange());
    }

    /**
     * Items collection setter
     * Syncs collection with storage
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {number[]} collection
     */
    set collection(collection: number[]) {
        this._collection = collection.splice(0, this.limit);

        this.app.events.broadcast(RecentlyViewed.EVENT, this);
    }

    /**
     * Items collection getter
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @return {number[]}
     */
    get collection(): number[] {
        return this._collection || [];
    }

    /**
     * Adds item to recently viewed collection
     * @param id {number}
     */
    private add(id: number): void {
        if (id) {
            const collection = without(this.collection, id);
            collection.unshift(id);
            this.collection = collection;
        }
    }

    /**
     * Removes item of given id from collection
     * @param  {number} id  item id
     */
    remove(id: number): void {
        this.collection = without(this.collection, id);
    }

    /**
     * Triggered on meta changes, updates list of items
     * if so then its id will be added to recently viewed collection
     */
    private onMetaChange() {
        const id = this.getCurrentProductTrackingId();
        if (id) {
            this.add(id);
        }
    }

    /**
     * Takes model id that is currently being viewed
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @return {number}
     */
    private getCurrentProductTrackingId(): number | undefined {
        const currentModel = <Product<IProductDataV3>>this.meta.model;
        if (currentModel?.type === Product.type && currentModel.data) {
            switch (recentlyViewedConfig.mode) {
                case 'child':
                    return currentModel.id;
                case 'top':
                    return currentModel.data.parent || currentModel.id;
            }
            return currentModel.id;
        }
    }

    /**
     * Gets all items as products
     * @returns {Product[]}
     */
    getItems() {
        return this.toProducts(this.collection);
    }

    /**
     * Get items without current product
     * @returns {Product[]}
     */
    getOtherItems() {
        let productIds = this.collection;
        if (productIds.length) {
            const id = this.getCurrentProductTrackingId();
            if (id) {
                productIds = without(this.collection, id);
            }
        }
        return this.toProducts(productIds);
    }

    /**
     * Removes all items from collection
     */
    removeAll(): void {
        this.collection = [];
    }

    /**
     * Retrieves products by ids
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {number[]} ids
     * @return {Product}
     */
    private toProducts(ids: number[]) {
        return this.app.resources.product.getAllExisting(ids);
    }

    /**
     * Load all recently viewed items
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {number} verbosity
     */
    load(verbosity: number = 2) {
        return this.app.resources.product.loadAllIds(this.collection, verbosity, { safe: true });
    }
}
